import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Avatar, Box, Card, CardContent, Chip, CircularProgress, Divider,
  FormControl, IconButton, MenuItem, Select, Skeleton, Table, TableBody,
  TableCell, TableHead, TableRow, Tab, Tabs, ToggleButton, ToggleButtonGroup,
  Tooltip as MuiTooltip, Typography,
} from '@mui/material';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, LabelList,
} from 'recharts';
import {
  ArrowBack, CalendarMonth, EventAvailable, Groups, InfoOutlined,
  Schedule, TableChart, BarChart as BarChartIcon, Star, StarBorder,
  FormatQuote,
} from '@mui/icons-material';
import apiService from '../services/apiService';
import ScheduleCalendar from '../components/instructor/ScheduleCalendar';
import AttendanceReportView from '../components/attendance/AttendanceReportView';
import { useAuth } from '../contexts/AuthContext';
import { attendanceApi } from '../services/attendanceApi';
import {
  AXIS_INK, DOW, GHOST, GHOST_INK, OTHER, PRIMARY, SERIES, STATUS,
  axisProps, gridProps,
} from '../components/instructor/vizTokens';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  INSTRUCTOR PERFORMANCE
 * ─────────────────────────────────────────────────────────────────────────────
 * Design: backend `docs/INSTRUCTOR_PERFORMANCE_DESIGN.md`.
 *
 * Everything on this screen comes from ONE endpoint, which derives every figure
 * from one resolved session set. This component does no arithmetic beyond
 * formatting and shaping for the chart library — if a number looks wrong the fix
 * belongs in the aggregation, not here. (The same rule StudioAnalytics states,
 * and for the same reason: a second place that computes is a second answer.)
 *
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
 * No attendance or no-show rate. The no-show cron runs for every studio whether
 * or not it checks anyone in, so at a studio that does not use check-in every
 * booking becomes NO_SHOW and every instructor would score 0% — a damning number
 * produced entirely by an unused feature. "Members taught" therefore means
 * members who BOOKED.
 *
 * ── THE READING WARNING IS PART OF THE UI ───────────────────────────────────
 * Fill rate is dominated by the slot and the class, not by the person: a 7am
 * beginner class fills differently from a 7pm signature ride. Every fill number
 * is shown against the studio's own average so it is read as a comparison rather
 * than a verdict, and the comparison table says so in words.
 */

/* ── formatting ─────────────────────────────────────────────────────────── */
const pct = (n: number | null | undefined) => (n == null ? '—' : `${n}%`);
const num = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString('en-IN');

const RANGES = [
  { key: '30d', label: '30 days', days: 29 },
  { key: '90d', label: '90 days', days: 89 },
  { key: '180d', label: '6 months', days: 179 },
  { key: '365d', label: '12 months', days: 364 },
] as const;

const istToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());

const cardSx = { height: '100%', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' } as const;

/* ── stat tile ──────────────────────────────────────────────────────────── */
const Stat: React.FC<{
  label: string;
  value: string;
  sub?: string;
  icon?: React.ReactNode;
  hint?: string;
  tone?: 'good' | 'warn' | 'bad' | null;
  loading?: boolean;
}> = ({ label, value, sub, icon, hint, tone, loading }) => (
  <Card sx={cardSx}>
    <CardContent sx={{ p: 2.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
          {hint && (
            <MuiTooltip title={hint} arrow>
              <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
            </MuiTooltip>
          )}
        </Box>
        {icon && (
          <Box sx={{
            width: 36, height: 36, borderRadius: 2, bgcolor: '#f6f6f8',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>{icon}</Box>
        )}
      </Box>
      {loading ? (
        <Skeleton variant="text" width="60%" height={40} />
      ) : (
        <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.15 }}>{value}</Typography>
      )}
      <Box minHeight={20} mt={0.5}>
        {sub && !loading && (
          <Typography
            variant="caption"
            sx={{ color: tone ? STATUS[tone] : 'text.secondary', fontWeight: tone ? 700 : 400 }}
          >
            {sub}
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

const Section: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, action, children }) => (
  <Card sx={cardSx}>
    <CardContent sx={{ p: 2.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={subtitle ? 0.5 : 2}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        {action}
      </Box>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          {subtitle}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

/** Shared tooltip chrome, so every chart on the page reads the same. */
const tipProps = {
  contentStyle: {
    borderRadius: 10,
    border: '1px solid #ececed',
    boxShadow: '0 6px 20px rgba(0,0,0,.10)',
    fontSize: 12,
  },
  cursor: { fill: 'rgba(0,0,0,.035)' },
} as const;

const InstructorPerformancePage: React.FC = () => {
  const { instructorId } = useParams<{ instructorId: string }>();
  const navigate = useNavigate();
  const { admin } = useAuth();
  // ?tab=attendance opens the staff attendance tab; anything else = performance.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsAttendance = searchParams.get('tab') === 'attendance';
  const isPlatformAdmin = admin?.role === 'super_admin' || admin?.role === 'admin';
  // The Attendance tab only exists once attendance is switched on for the
  // studio (Studio → Permissions).
  const [attendanceOn, setAttendanceOn] = useState(false);
  const tab: 'performance' | 'attendance' = wantsAttendance && attendanceOn ? 'attendance' : 'performance';

  const [rangeKey, setRangeKey] = useState<typeof RANGES[number]['key']>('90d');
  const [data, setData] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableView, setTableView] = useState(false);

  const range = useMemo(() => {
    const days = RANGES.find((r) => r.key === rangeKey)!.days;
    const end = istToday();
    const start = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' })
      .format(new Date(Date.now() - days * 864e5));
    return { startDate: start, endDate: end };
  }, [rangeKey]);

  const load = useCallback(async () => {
    if (!instructorId) return;
    setLoading(true);
    setError(null);
    try {
      // Sequential, not parallel, and deliberately so: the summary needs a
      // studioId, and a SUPER ADMIN has no studio on its token to supply one.
      // The performance response carries the instructor's own studioId, so it
      // has to land first. A studio token ignores the param and stays scoped by
      // its guard either way.
      const perf: any = await apiService.getInstructorPerformance(instructorId, range);
      setData(perf);

      const sum = await apiService
        .getInstructorPerformanceSummary({
          ...range,
          studioId: perf?.instructor?.studioId,
        })
        .catch(() => null); // comparison is a bonus; never fail the page for it
      setSummary(sum);
    } catch (e: any) {
      setError(e?.message || 'Could not load performance');
    } finally {
      setLoading(false);
    }
  }, [instructorId, range]);

  useEffect(() => { load(); }, [load]);

  const attendanceStudioId: string | undefined = data?.instructor?.studioId;
  useEffect(() => {
    if (isPlatformAdmin && !attendanceStudioId) return;
    let live = true;
    attendanceApi
      .getStudioSettings(isPlatformAdmin ? attendanceStudioId : undefined)
      .then((s) => live && setAttendanceOn(!!s?.enabled))
      .catch(() => live && setAttendanceOn(false));
    return () => { live = false; };
  }, [isPlatformAdmin, attendanceStudioId]);

  const w = data?.workload;
  const d = data?.demand;
  const m = data?.members;
  const q = data?.quality ?? { ratingCount: 0, commentCount: 0, recent: [], distribution: {} };

  /**
   * The headline demand number, chosen by the kind of work the instructor is
   * actually given.
   *
   * A blended seat-fill is the wrong headline for a roster that is mostly 1:1.
   * A private session holds ONE person — filling it is the whole success, not
   * "one of eight seats" — and a studio that publishes an open private slot for
   * every bookable hour will have hundreds of untaken ones that say nothing
   * about the instructor. At Purple Pilates that blend reads 18%, while the
   * group classes are at 36% and 87% of them had someone in the room.
   *
   * So: group-heavy → seats. One-to-one-heavy → slots taken. Both shown either
   * way; only the emphasis moves.
   */
  const headline = useMemo(() => {
    const byType: any[] = d?.byType ?? [];
    const oneToOne = byType.filter((t) => t.key !== 'group').reduce((a, t) => a + t.sessions, 0);
    const group = byType.find((t) => t.key === 'group')?.sessions ?? 0;
    const leadOneToOne = oneToOne > group;

    const value = leadOneToOne ? d?.oneToOneBookedRate : d?.groupFillRate;
    const bench = leadOneToOne ? summary?.studioOneToOneBookedRate : summary?.studioGroupFillRate;
    const delta = value == null || bench == null ? null : value - bench;

    return {
      label: leadOneToOne ? 'Private slots taken' : 'Group fill rate',
      value,
      tone: delta == null ? null : delta >= 0 ? ('good' as const) : ('warn' as const),
      sub:
        delta == null
          ? leadOneToOne
            ? `${num(oneToOne)} 1:1 sessions offered`
            : `${num(d?.totalBookings)} of ${num(d?.capacity)} seats`
          : `${delta >= 0 ? '+' : ''}${delta} pts vs studio average (${pct(bench)})`,
      hint: leadOneToOne
        ? 'Private and duet sessions that were taken, out of those offered. A 1:1 slot holds one person, so filling it is the success — measuring it in seats would make a booked private look like a nearly-empty class.'
        : 'Seats booked ÷ seats offered, GROUP classes only. Private and duet are counted separately, in slots taken — averaging the two produces a number that describes how much availability the studio publishes rather than how the instructor performs.',
    };
  }, [d, summary]);

  /** Class mix, capped at five real slots + a grey "Other" — never a 6th hue. */
  const classMix = useMemo(() => {
    const rows: any[] = w?.byClassType ?? [];
    const top = rows.slice(0, 5).map((r, i) => ({ ...r, color: SERIES[i] }));
    const rest = rows.slice(5);
    if (rest.length) {
      top.push({
        id: 'other',
        label: `Other (${rest.length})`,
        sessions: rest.reduce((a, r) => a + r.sessions, 0),
        bookings: rest.reduce((a, r) => a + r.bookings, 0),
        capacity: rest.reduce((a, r) => a + r.capacity, 0),
        color: OTHER,
      });
    }
    return top;
  }, [w]);

  /** Only hours the instructor actually works — 24 mostly-empty bars is noise. */
  const hours = useMemo(() => {
    const rows: any[] = w?.byHourOfDay ?? [];
    const live = rows.filter((r) => r.sessions > 0);
    if (!live.length) return [];
    const lo = Math.max(0, Math.min(...live.map((r) => r.hour)) - 1);
    const hi = Math.min(23, Math.max(...live.map((r) => r.hour)) + 1);
    return rows.slice(lo, hi + 1).map((r) => ({
      ...r,
      label: `${String(r.hour).padStart(2, '0')}`,
    }));
  }, [w]);

  const dows = useMemo(
    () => (w?.byDayOfWeek ?? []).map((r: any) => ({ ...r, label: DOW[r.dow] })),
    [w],
  );

  const series = data?.series ?? [];

  if (!instructorId) return null;

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        {/* Back to wherever they came from — the studio's Instructors tab, or
            the global list. A hardcoded '/instructors' sent a super admin who
            arrived from a studio page somewhere they had never been. */}
        <IconButton
          onClick={() =>
            window.history.length > 1 ? navigate(-1) : navigate('/instructors')
          }
          size="small"
        >
          <ArrowBack />
        </IconButton>
        <Avatar src={data?.instructor?.image} sx={{ width: 52, height: 52, bgcolor: PRIMARY }}>
          {data?.instructor?.name?.charAt(0) ?? 'I'}
        </Avatar>
        <Box flexGrow={1} minWidth={200}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h5" fontWeight={800}>
              {loading && !data ? <Skeleton width={180} /> : data?.instructor?.name ?? 'Instructor'}
            </Typography>
            {data?.instructor?.isDisabled && (
              <Chip size="small" label="Disabled" color="default" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {data?.instructor?.role || 'Performance'}
            {data?.range && ` · ${data.range.startDate} → ${data.range.endDate}`}
          </Typography>
        </Box>

        {/* Switch instructor without going back to the list. */}
        {summary?.instructors?.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={instructorId}
              onChange={(e) => navigate(`/instructors/${e.target.value}`)}
            >
              {summary.instructors.map((i: any) => (
                <MenuItem key={i._id} value={i._id}>{i.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {tab === 'performance' && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={rangeKey}
            onChange={(_, v) => v && setRangeKey(v)}
          >
            {RANGES.map((r) => (
              <ToggleButton key={r.key} value={r.key} sx={{ px: 1.5 }}>{r.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>

      {attendanceOn && (
      <Tabs
        value={tab}
        onChange={(_, v) => {
          const next = new URLSearchParams(searchParams);
          if (v === 'attendance') next.set('tab', 'attendance');
          else next.delete('tab');
          setSearchParams(next, { replace: true });
        }}
        sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="performance" label="Performance" />
        <Tab value="attendance" label="Attendance" />
      </Tabs>
      )}

      {tab === 'attendance' && (
        // A super admin has no studio on its token; the performance response
        // carries the instructor's studioId, so wait for it.
        isPlatformAdmin && !data?.instructor?.studioId ? (
          loading ? <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
            : <Alert severity="error">{error || "Couldn't find this instructor's studio."}</Alert>
        ) : (
          <AttendanceReportView
            instructorId={instructorId}
            studioId={isPlatformAdmin ? data?.instructor?.studioId : undefined}
            role={admin?.role}
          />
        )
      )}

      {tab === 'performance' && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {tab !== 'performance' ? null : loading && !data ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : !data ? null : (
        <Box display="flex" flexDirection="column" gap={2.5}>

          {/* ── Stat tiles ───────────────────────────────────────────── */}
          <Box display="grid" gap={2.5} gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', lg: 'repeat(5, 1fr)' }}>
            <Stat
              label="Sessions taught"
              value={num(w.sessionsHeld)}
              sub={`${num(w.sessionsScheduled)} scheduled · ${w.sessionsScheduled - w.sessionsHeld} with no bookings`}
              icon={<EventAvailable sx={{ fontSize: 20, color: PRIMARY }} />}
              hint="“Taught” counts sessions with at least one booking. Nothing marks a class as held, and a cancelled class is deleted outright — so “scheduled” is not a complete record of what was committed to."
            />
            <Stat
              label="Hours taught"
              value={`${num(w.hoursTaught)}h`}
              sub={`across ${num(w.daysWorked)} day${w.daysWorked === 1 ? '' : 's'}`}
              icon={<Schedule sx={{ fontSize: 20, color: PRIMARY }} />}
            />
            <Stat
              label={headline.label}
              value={pct(headline.value)}
              sub={headline.sub}
              tone={headline.tone}
              icon={<BarChartIcon sx={{ fontSize: 20, color: PRIMARY }} />}
              hint={headline.hint}
            />
            <Stat
              label="Members reached"
              value={num(m.unique)}
              sub={`${pct(m.repeatRate)} came back · ${num(m.newToStudio)} new to the studio`}
              icon={<Groups sx={{ fontSize: 20, color: PRIMARY }} />}
              hint="Members who BOOKED one of their sessions. Check-in data is not used — the no-show cron runs at every studio whether or not it checks anyone in, so attendance would read 0% at most studios."
            />
            <Stat
              label="Rating"
              value={q.avgRating == null ? '—' : `${q.avgRating}`}
              sub={
                q.ratingCount === 0
                  ? 'no ratings yet'
                  : `${num(q.ratingCount)} rating${q.ratingCount === 1 ? '' : 's'}${q.commentCount ? ` · ${num(q.commentCount)} comment${q.commentCount === 1 ? '' : 's'}` : ''}`
              }
              icon={<Star sx={{ fontSize: 20, color: '#F5A623' }} />}
              hint="The class rating members leave in the app — asked against this instructor's name, so it is their score, not the studio's. Reviews left before the two-part rating shipped carry a studio score only and cannot be attributed to anyone."
            />
          </Box>

          {/* ── Ratings and comments ─────────────────────────────────────
              Placed directly under the KPI row, ahead of the charts. What a
              member actually said about the class is the first thing anyone
              opens this page to read; the seat/fill analytics explain it and
              belong after it. ─────────────────────────────────────────── */}
          <Section
            title="What members said"
            subtitle="The class rating members give in the app IS the instructor's score — it is asked against their name and face. The studio's own rating is a separate question and is not shown here."
          >
            {q.ratingCount === 0 && q.commentCount === 0 ? (
              <Alert severity="info" icon={<InfoOutlined />} sx={{ borderRadius: 2 }}>
                No ratings yet for this period. Per-instructor ratings started
                being collected when the two-part rating (studio + class) shipped —
                reviews left before that carry a studio score only and cannot be
                attributed to anyone, so this stays empty until new ones come in.
              </Alert>
            ) : (
              <Box display="grid" gap={3} gridTemplateColumns={{ xs: '1fr', md: '260px 1fr' }}>
                {/* Score + distribution */}
                <Box>
                  <Box display="flex" alignItems="baseline" gap={1}>
                    <Typography variant="h2" fontWeight={800} sx={{ lineHeight: 1 }}>
                      {q.avgRating ?? '—'}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">/ 5</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.25} mt={0.5}>
                    {[1, 2, 3, 4, 5].map((i) =>
                      (q.avgRating ?? 0) >= i - 0.5 ? (
                        <Star key={i} sx={{ fontSize: 18, color: '#F5A623' }} />
                      ) : (
                        <StarBorder key={i} sx={{ fontSize: 18, color: '#c9c9cf' }} />
                      ),
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                    {num(q.ratingCount)} rating{q.ratingCount === 1 ? '' : 's'}
                    {q.commentCount > 0 && ` · ${num(q.commentCount)} with a comment`}
                  </Typography>

                  <Box mt={2}>
                    {[5, 4, 3, 2, 1].map((star) => {
                      const n = q.distribution?.[star] ?? 0;
                      const share = q.ratingCount ? (n / q.ratingCount) * 100 : 0;
                      return (
                        <Box key={star} display="flex" alignItems="center" gap={1} mb={0.6}>
                          <Typography variant="caption" sx={{ width: 10 }}>{star}</Typography>
                          <Star sx={{ fontSize: 12, color: '#c9c9cf' }} />
                          <Box flex={1} display="flex" height={7} borderRadius={1} overflow="hidden" gap="2px">
                            <Box sx={{ flex: Math.max(0.0001, share), bgcolor: '#F5A623' }} />
                            <Box sx={{ flex: Math.max(0.0001, 100 - share), bgcolor: GHOST }} />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 22, textAlign: 'right' }}>
                            {n}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* The comments themselves */}
                <Box>
                  {(q.recent ?? []).length === 0 ? (
                    <Typography variant="body2" color="text.disabled">
                      Ratings but no written comments yet.
                    </Typography>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      {q.recent.map((r: any) => (
                        <Box key={r._id} sx={{ p: 1.75, borderRadius: 2, bgcolor: '#fafafb' }}>
                          <Box display="flex" alignItems="center" gap={1} mb={r.text ? 0.75 : 0}>
                            <Box display="flex" alignItems="center" gap={0.2}>
                              {r.rating == null ? (
                                <Typography variant="caption" color="text.disabled">
                                  comment only
                                </Typography>
                              ) : (
                                [1, 2, 3, 4, 5].map((i) =>
                                  i <= r.rating ? (
                                    <Star key={i} sx={{ fontSize: 14, color: '#F5A623' }} />
                                  ) : (
                                    <StarBorder key={i} sx={{ fontSize: 14, color: '#d6d6db' }} />
                                  ),
                                )
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {[r.memberName, r.className].filter(Boolean).join(' · ')}
                            </Typography>
                            <Box flexGrow={1} />
                            <Typography variant="caption" color="text.disabled">
                              {r.sessionDate
                                ? new Date(r.sessionDate).toLocaleDateString('en-GB', {
                                    day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata',
                                  })
                                : ''}
                            </Typography>
                          </Box>
                          {r.text && (
                            <Box display="flex" gap={0.75}>
                              <FormatQuote sx={{ fontSize: 15, color: '#c9c9cf', transform: 'scaleX(-1)', mt: 0.2 }} />
                              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                {r.text}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Section>

          {/* ── Small multiples: seats, then fill. Shared x, one axis each. ── */}
          <Box display="grid" gap={2.5} gridTemplateColumns={{ xs: '1fr', lg: '3fr 2fr' }}>
            <Section
              title="Seats offered and booked"
              subtitle="Both series are seats, on one axis — the gap between them IS the empty space. (Plotting seats and a percentage together would need two scales, which invents a relationship that is not in the data.)"
            >
              <ResponsiveContainer width="100%" height={230}>
                <AreaChart data={series} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...axisProps} tickFormatter={(v) => String(v).slice(5)} minTickGap={28} />
                  <YAxis {...axisProps} allowDecimals={false} />
                  <Tooltip {...tipProps} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
                  <Area
                    type="monotone" dataKey="capacity" name="Seats offered"
                    stroke={GHOST_INK} strokeWidth={1} fill={GHOST} fillOpacity={1}
                  />
                  <Area
                    type="monotone" dataKey="bookings" name="Seats booked"
                    stroke={PRIMARY} strokeWidth={2} fill={PRIMARY} fillOpacity={0.22}
                  />
                </AreaChart>
              </ResponsiveContainer>

              {/* The facts the area chart implies but does not state. */}
              <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }}
                gap={1.5}
                mt={2}
                pt={2}
                borderTop="1px solid #f0f0f2"
              >
                {[
                  {
                    l: 'Avg class size',
                    v: num(d.avgClassSize),
                    h: `Averaged over the ${num(w.sessionsHeld)} sessions that had at least one booking. Across all ${num(w.sessionsScheduled)} scheduled it is ${num(d.avgAcrossScheduled)} — a studio that publishes an open slot for every bookable hour will see a much lower figure there, which is availability rather than an empty class.`,
                  },
                  { l: 'Sold out', v: num(d.soldOut), h: 'Sessions where every seat was taken.' },
                  { l: 'No bookings', v: num(d.empty), h: 'Sessions that ended with nobody booked.' },
                  { l: 'Waitlisted', v: num(d.waitlisted), h: 'Members who joined a waitlist for one of their sessions — demand the room could not hold.' },
                ].map((x) => (
                  <Box key={x.l}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>{x.l}</Typography>
                      <MuiTooltip title={x.h} arrow>
                        <InfoOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
                      </MuiTooltip>
                    </Box>
                    <Typography variant="h6" fontWeight={800}>{x.v}</Typography>
                  </Box>
                ))}
              </Box>
            </Section>

            <Section
              title="Fill rate over time"
              subtitle="The same days as a percentage. The dashed rule is the studio's own average — the line to read this against."
            >
              <ResponsiveContainer width="100%" height={230}>
                <LineChart data={series} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...axisProps} tickFormatter={(v) => String(v).slice(5)} minTickGap={28} />
                  <YAxis {...axisProps} domain={[0, 100]} unit="%" />
                  <Tooltip {...tipProps} formatter={(v: any) => [`${v}%`, 'Fill rate']} />
                  {summary?.studioFillRate != null && (
                    <ReferenceLine
                      y={summary.studioFillRate}
                      stroke={AXIS_INK}
                      strokeDasharray="4 4"
                      label={{ value: `studio avg ${summary.studioFillRate}%`, position: 'insideTopRight', fontSize: 10, fill: AXIS_INK }}
                    />
                  )}
                  {/* One series — no legend box; the title names it. */}
                  <Line
                    type="monotone" dataKey="fillRate" name="Fill rate"
                    stroke={PRIMARY} strokeWidth={2} dot={false}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Section>
          </Box>

          {/* ── Demand, split by what the session actually is ─────────── */}
          {(d.byType ?? []).length > 0 && (
            <Section
              title="Demand by session type"
              subtitle="A group class is measured in seats; a private or duet is measured in whether the slot was taken at all. They are different units and are never averaged together."
            >
              <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: `repeat(${Math.min(3, d.byType.length)}, 1fr)` }}>
                {d.byType.map((t: any, i: number) => {
                  const isGroup = t.primaryMetric === 'fillRate';
                  const v = isGroup ? t.fillRate : t.bookedRate;
                  return (
                    <Box key={t.key} sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafb' }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SERIES[i] }} />
                        <Typography variant="body2" fontWeight={700} textTransform="capitalize">
                          {t.key}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {num(t.sessions)} sessions
                        </Typography>
                      </Box>
                      <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                        {pct(v)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {isGroup
                          ? `${num(t.bookings)} of ${num(t.capacity)} seats booked`
                          : `${num(t.booked)} of ${num(t.sessions)} slots taken`}
                      </Typography>
                      {/* Direct label + a bar, never colour alone. */}
                      <Box display="flex" height={8} borderRadius={1} overflow="hidden" gap="2px" mt={1.5}>
                        <Box sx={{ flex: Math.max(0.0001, v ?? 0), bgcolor: SERIES[i] }} />
                        <Box sx={{ flex: Math.max(0.0001, 100 - (v ?? 0)), bgcolor: GHOST }} />
                      </Box>
                      {!isGroup && (
                        <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                          Seat fill would read {pct(t.fillRate)} — not meaningful for a {t.key}.
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {data.dataQuality?.oneToOneCapacityLooksInherited && (
                <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2, borderRadius: 2 }}>
                  Some private or duet sessions are set up with more seats than that
                  format can hold — capacity inherited from the parent class rather
                  than set for a 1:1. Their seat-fill percentage is therefore
                  meaningless, which is why slot take-up is shown instead. Worth
                  correcting the class setup so both numbers agree.
                </Alert>
              )}
            </Section>
          )}

          {/* ── When they teach ──────────────────────────────────────── */}
          <Box display="grid" gap={2.5} gridTemplateColumns={{ xs: '1fr', md: '2fr 3fr' }}>
            <Section title="Days of the week" subtitle="Sessions taught, by weekday.">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={dows} margin={{ top: 14, right: 8, left: -22, bottom: 0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="label" {...axisProps} />
                  <YAxis {...axisProps} allowDecimals={false} />
                  <Tooltip {...tipProps} />
                  {/* One series → one colour. Never a value-ramp on the bars:
                      that double-encodes length as hue and buys nothing. */}
                  <Bar dataKey="sessions" name="Sessions" fill={PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={30}>
                    <LabelList dataKey="sessions" position="top" style={{ fontSize: 10, fill: AXIS_INK }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Section>

            <Section title="Time of day" subtitle="Sessions by start hour, IST. Only hours they actually work are shown.">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={hours} margin={{ top: 14, right: 8, left: -22, bottom: 0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="label" {...axisProps} />
                  <YAxis {...axisProps} allowDecimals={false} />
                  <Tooltip {...tipProps} labelFormatter={(l) => `${l}:00 IST`} />
                  <Bar dataKey="sessions" name="Sessions" fill={PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </Section>
          </Box>

          {/* ── Class mix + members ──────────────────────────────────── */}
          <Box display="grid" gap={2.5} gridTemplateColumns={{ xs: '1fr', md: '3fr 2fr' }}>
            <Section
              title="What they teach"
              subtitle="Sessions by class type, with how full each ran. Every bar is labelled — colour alone never carries the value."
            >
              <ResponsiveContainer width="100%" height={Math.max(160, classMix.length * 44)}>
                <BarChart data={classMix} layout="vertical" margin={{ top: 0, right: 56, left: 8, bottom: 0 }}>
                  <CartesianGrid {...gridProps} horizontal={false} vertical />
                  <XAxis type="number" {...axisProps} allowDecimals={false} />
                  <YAxis type="category" dataKey="label" {...axisProps} width={120} />
                  <Tooltip
                    {...tipProps}
                    formatter={(v: any, _n: any, p: any) => [
                      `${v} sessions · ${p.payload.bookings} booked of ${p.payload.capacity} (${pct(p.payload.fillRate)})`,
                      p.payload.label,
                    ]}
                  />
                  <Bar dataKey="sessions" radius={[0, 4, 4, 0]} maxBarSize={22}>
                    {classMix.map((c) => <Cell key={c.id} fill={c.color} />)}
                    <LabelList
                      dataKey="fillRate"
                      position="right"
                      formatter={(v: any) => (v == null ? '' : `${v}% full`)}
                      style={{ fontSize: 11, fill: AXIS_INK, fontWeight: 600 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Section>

            <Section
              title="Members"
              subtitle="Counted by bookings, not check-ins."
            >
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                {[
                  { l: 'Unique', v: num(m.unique), h: 'Distinct members who booked one of their sessions.' },
                  { l: 'Came back', v: `${num(m.repeat)} (${pct(m.repeatRate)})`, h: 'Booked them two or more times in the range.' },
                  { l: 'New to studio', v: num(m.newToStudio), h: "Their very first booking at this studio was one of this instructor's." },
                  { l: 'Regulars', v: num(m.followers), h: 'Members for whom more than half of all their bookings at this studio are with this instructor.' },
                ].map((s) => (
                  <Box key={s.l}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>{s.l}</Typography>
                      <MuiTooltip title={s.h} arrow>
                        <InfoOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
                      </MuiTooltip>
                    </Box>
                    <Typography variant="h6" fontWeight={800}>{s.v}</Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Trial classes
              </Typography>
              {m.trialsTaught === 0 ? (
                <Typography variant="body2" color="text.disabled" mt={0.5}>
                  No trials taught in this range.
                </Typography>
              ) : (
                <>
                  <Box display="flex" alignItems="baseline" gap={1} mt={0.5} mb={1}>
                    <Typography variant="h5" fontWeight={800}>{pct(m.conversionRate)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.trialsConverted} of {m.trialsTaught} went on to buy a pack
                    </Typography>
                  </Box>
                  {/* Two parts of a whole, so a bar — not a pie, and not a funnel. */}
                  <Box display="flex" height={12} borderRadius={1} overflow="hidden" gap="2px">
                    <Box sx={{ flex: m.trialsConverted || 0.0001, bgcolor: PRIMARY }} />
                    <Box sx={{ flex: Math.max(0.0001, m.trialsTaught - m.trialsConverted), bgcolor: GHOST }} />
                  </Box>
                </>
              )}
            </Section>
          </Box>

          {/* ── Calendar ─────────────────────────────────────────────── */}
          <Section
            title="Schedule"
            subtitle="Every day they taught, coloured by how full that day's classes ran. Click a day for the session list."
            action={<CalendarMonth sx={{ color: 'text.disabled' }} />}
          >
            {data.calendar?.length ? (
              <ScheduleCalendar days={data.calendar} />
            ) : (
              <Typography variant="body2" color="text.disabled">
                No sessions in this range.
              </Typography>
            )}
          </Section>

          {/* ── Comparison + the table view ──────────────────────────── */}
          {summary?.instructors?.length > 0 && (
            <Section
              title="All instructors"
              subtitle="Group fill rate only — a roster that is mostly private would rank last on a blended number for reasons that have nothing to do with the instructor. The table view carries both measures. Read this as a description of the schedule, not a ranking of people."
              action={
                <ToggleButtonGroup
                  size="small" exclusive value={tableView ? 'table' : 'chart'}
                  onChange={(_, v) => v && setTableView(v === 'table')}
                >
                  <ToggleButton value="chart"><BarChartIcon sx={{ fontSize: 16 }} /></ToggleButton>
                  <ToggleButton value="table"><TableChart sx={{ fontSize: 16 }} /></ToggleButton>
                </ToggleButtonGroup>
              }
            >
              {tableView ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Instructor</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                      <TableCell align="right">Hours</TableCell>
                      <TableCell align="right">Seats booked</TableCell>
                      <TableCell align="right">Seats offered</TableCell>
                      <TableCell align="right">Group fill</TableCell>
                      <TableCell align="right">1:1 taken</TableCell>
                      <TableCell align="right">Members</TableCell>
                      <TableCell align="right">Rating</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.instructors.map((i: any) => (
                      <TableRow
                        key={i._id}
                        hover
                        sx={{ cursor: 'pointer', bgcolor: i._id === instructorId ? '#fdf2f6' : undefined }}
                        onClick={() => navigate(`/instructors/${i._id}`)}
                      >
                        <TableCell>
                          {i.name}{i.isDisabled && <Chip size="small" label="disabled" sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell align="right">{num(i.sessions)}</TableCell>
                        <TableCell align="right">{num(i.hoursTaught)}</TableCell>
                        <TableCell align="right">{num(i.bookings)}</TableCell>
                        <TableCell align="right">{num(i.capacity)}</TableCell>
                        <TableCell align="right">
                          {pct(i.groupFillRate)}
                          {i.groupSessions > 0 && (
                            <Typography variant="caption" color="text.disabled" display="block">
                              {num(i.groupSessions)} grp
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {pct(i.oneToOneBookedRate)}
                          {i.oneToOneSessions > 0 && (
                            <Typography variant="caption" color="text.disabled" display="block">
                              {num(i.oneToOneSessions)} 1:1
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{num(i.uniqueMembers)}</TableCell>
                        <TableCell align="right">
                          {i.avgRating == null ? (
                            <Typography variant="caption" color="text.disabled">
                              not rated
                            </Typography>
                          ) : (
                            <>
                              {i.avgRating}
                              <Typography variant="caption" color="text.disabled" display="block">
                                {i.ratingCount}
                              </Typography>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(180, summary.instructors.length * 34)}>
                  <BarChart
                    data={summary.instructors}
                    layout="vertical"
                    margin={{ top: 0, right: 48, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid {...gridProps} horizontal={false} vertical />
                    <XAxis type="number" {...axisProps} domain={[0, 100]} unit="%" />
                    <YAxis type="category" dataKey="name" {...axisProps} width={130} />
                    <Tooltip
                      {...tipProps}
                      formatter={(v: any, _n: any, p: any) => [
                        `${v == null ? '—' : `${v}%`} of group seats · ${p.payload.groupSessions} group, ${p.payload.oneToOneSessions} 1:1 sessions`,
                        p.payload.name,
                      ]}
                    />
                    {summary.studioGroupFillRate != null && (
                      <ReferenceLine
                        x={summary.studioGroupFillRate}
                        stroke={AXIS_INK}
                        strokeDasharray="4 4"
                        label={{ value: 'studio avg', position: 'top', fontSize: 10, fill: AXIS_INK }}
                      />
                    )}
                    <Bar dataKey="groupFillRate" radius={[0, 4, 4, 0]} maxBarSize={18}>
                      {/* Emphasis, not eight hues: the instructor being viewed is
                          highlighted and everyone else recedes. Colour follows
                          the entity in view, never the row's rank. */}
                      {summary.instructors.map((i: any) => (
                        <Cell key={i._id} fill={i._id === instructorId ? PRIMARY : '#d9d9de'} />
                      ))}
                      <LabelList
                        dataKey="groupFillRate"
                        position="right"
                        formatter={(v: any) => (v == null ? '—' : `${v}%`)}
                        style={{ fontSize: 11, fill: AXIS_INK, fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Section>
          )}

          <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>
            Sessions are attributed to whoever was on the session itself, not the class —
            so a cover is credited to the person who actually taught it. Cancelled classes
            are deleted outright and leave no trace, so “scheduled” is not a complete record.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default InstructorPerformancePage;
